import { Input } from "../../../../components/ui/input";
import { Plus } from "lucide-react";

/**
 * RuleChainEditor — Option A · Pt 3 composable action chain builder.
 *
 * Renders the "AND ALSO" panel inside the automation-rule editor dialog.
 * Each step in `ruleDraft.actions` is a typed side-effect
 * (route / tag / warehouse / insurance / playbook) that the backend
 * `_merge_action_chain()` folds into a single action dict.
 *
 * Props are lifted from the parent (SellerDropship) so state stays
 * co-located with the rest of the rule draft.
 */
export default function RuleChainEditor({
  ruleDraft, setRuleDraft, rulesMeta, settings, playbooks,
}) {
  const steps = ruleDraft.actions || [];
  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="rule-chain-editor">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">AND ALSO · action chain</div>
          <div className="text-[10.5px] text-foreground/50 mt-0.5">Runs in order. Later steps override earlier ones for the same field. Tags accumulate.</div>
        </div>
        <button
          onClick={() => setRuleDraft(d => ({
            ...d,
            actions: [...(d.actions || []), { type: "tag", tags: [] }],
          }))}
          data-testid="rule-chain-add"
          className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-8 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
          <Plus className="w-3 h-3"/>Add step
        </button>
      </div>
      {steps.length === 0 ? (
        <div className="text-[11.5px] text-foreground/50 italic">
          No chain steps. The rule will only apply the primary action above.
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((step, idx) => {
            const setStep = (patch) => setRuleDraft(d => ({
              ...d,
              actions: (d.actions || []).map((s, i) => i === idx ? { ...s, ...patch } : s),
            }));
            const removeStep = () => setRuleDraft(d => ({
              ...d,
              actions: (d.actions || []).filter((_, i) => i !== idx),
            }));
            return (
              <div key={idx} className="rounded-lg border border-border bg-secondary/30 p-3 space-y-2" data-testid={`rule-chain-step-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 w-6">#{idx + 1}</span>
                  <select value={step.type || "tag"}
                    onChange={e => setStep({ type: e.target.value })}
                    data-testid={`rule-chain-type-${idx}`}
                    className="h-9 rounded-lg border border-border bg-white px-2 text-[12.5px]">
                    {(rulesMeta.allowed_action_types || []).map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <div className="flex-1"/>
                  <button onClick={removeStep}
                    data-testid={`rule-chain-remove-${idx}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-rose-50 hover:border-rose-200 text-rose-600">
                    Remove
                  </button>
                </div>
                {step.type === "route" && (
                  <div className="grid grid-cols-2 gap-2">
                    <select value={step.carrier || ""}
                      onChange={e => setStep({ carrier: e.target.value })}
                      className="h-9 rounded-lg border border-border bg-white px-2 text-[12.5px]">
                      <option value="">— carrier</option>
                      <option value="usps">USPS</option>
                      <option value="ups">UPS</option>
                      <option value="fedex">FedEx</option>
                      <option value="dhl_express">DHL Express</option>
                    </select>
                    <Input value={step.service_token || ""}
                      onChange={e => setStep({ service_token: e.target.value })}
                      placeholder="service token"
                      className="h-9 mono"/>
                  </div>
                )}
                {step.type === "tag" && (
                  <Input value={(step.tags || []).join(", ")}
                    onChange={e => setStep({ tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                    placeholder="heavy, west-coast, vip"
                    data-testid={`rule-chain-tags-${idx}`}
                    className="h-9 mono"/>
                )}
                {step.type === "warehouse" && (
                  <select value={step.ship_from_address_id || ""}
                    onChange={e => setStep({ ship_from_address_id: e.target.value })}
                    className="w-full h-9 rounded-lg border border-border bg-white px-2 text-[12.5px]">
                    <option value="">— pick warehouse</option>
                    {(settings.ship_from_addresses || []).map(w => (
                      <option key={w.id} value={w.id}>{w.name || w.street1}</option>
                    ))}
                  </select>
                )}
                {step.type === "insurance" && (
                  <Input type="number" min="0" step="10"
                    value={step.min_insurance || ""}
                    onChange={e => setStep({ min_insurance: Number(e.target.value || 0) })}
                    placeholder="min insurance $"
                    className="h-9 mono w-40"/>
                )}
                {step.type === "playbook" && (
                  <select value={step.playbook_id || ""}
                    onChange={e => setStep({ playbook_id: e.target.value })}
                    className="w-full h-9 rounded-lg border border-border bg-white px-2 text-[12.5px]">
                    <option value="">— pick playbook</option>
                    {playbooks.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
